import { firestore, server } from "../../config/ConfigFirebase";
import { getToken } from "../auth/AuthServices";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { PQRSD_STATUS, PQRSD_SLA_DAYS } from "../../constants";
import {
  notifyStaffAssigned,
  notifyStaffForwarded,
  notifyStaffReturned,
  notifyStaffReassigned,
  notifyCitizenInProgress,
} from "./PqrsdEmailService";

import { pqrsdText } from "../../components/pqrsd/pqrsdLabel";
// Local copy of the Bogota-business-day calculator used server-side too
// (see gfunctions/pqrsd/_shared/businessDays.js). We need it on the client for
// optimistic SLA resets when an admin reassigns a returned PQRSD.
const addBusinessDays = (date, days) => {
  // Lightweight version — skip weekends. Holidays are handled server-side
  // by pqrsdSlaSweep; this client-side compute is only for optimistic UI.
  const d = new Date(date);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added += 1;
  }
  return d;
};

// PQRSD lives inside the existing `filed` collection, keyed by radicado. The
// `filedType: "pqrsd"` marker lets us reuse every filed surface while keeping
// the PQRSD-specific fields on the `pqrsd` sub-map.
const COLLECTION = "filed";

// Builds a Cloud Function URL for the current environment. `server` is
// `{region}-{projectId}` (e.g. "us-east1-royalplusdev"), so each environment
// resolves to its OWN project's functions without hardcoding per-env URLs.
// Used as the fallback when the explicit REACT_APP_PQRSD_* env override is unset.
export const pqrsdFunctionUrl = (fn) =>
  `https://${server}.cloudfunctions.net/${fn}`;

// Firestore Timestamps and JS Date objects are non-serializable for Redux. We
// normalize every timestamp on the PQRSD document to an ISO string so the slice
// stays clean and the dev-mode serializableCheck middleware doesn't warn. All
// formatters in components already accept ISO strings via `new Date(value)`.
const toIso = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (v.toDate) return v.toDate().toISOString();
  if (v instanceof Date) return v.toISOString();
  return null;
};

const mapFiled = (snap) => {
  const data = snap.data();
  const p = data.pqrsd || {};
  const responses = Array.isArray(p.responses)
    ? p.responses.map((r) => ({
        ...r,
        filedAt: toIso(r.filedAt),
        citizenActionAt: toIso(r.citizenActionAt),
      }))
    : [];
  const activeResponseFlows = Array.isArray(p.activeResponseFlows)
    ? p.activeResponseFlows.map((f) => ({ ...f, startedAt: toIso(f.startedAt) }))
    : [];
  return {
    ...data,
    filedId: data.filedId || snap.id,
    createdAt: toIso(data.createdAt),
    doneAt: toIso(data.doneAt),
    pqrsd: {
      ...p,
      slaDeadline: toIso(p.slaDeadline),
      slaBaseDate: toIso(p.slaBaseDate),
      slaWarningSentAt: toIso(p.slaWarningSentAt),
      slaBreachedNotifiedAt: toIso(p.slaBreachedNotifiedAt),
      assignedAt: toIso(p.assignedAt),
      groupAssignedAt: toIso(p.groupAssignedAt),
      respondedAt: toIso(p.respondedAt),
      closedAt: toIso(p.closedAt),
      responseFlowStartedAt: toIso(p.responseFlowStartedAt),
      responseFlowApprovedAt: toIso(p.responseFlowApprovedAt),
      responseFlowRejectedAt: toIso(p.responseFlowRejectedAt),
      citizenClosedAt: toIso(p.citizenClosedAt),
      reopenedAt: toIso(p.reopenedAt),
      // Requerimiento de información (CPACA art. 17) — normalized like every
      // other timestamp so the slice stays serializable.
      slaPausedAt: toIso(p.slaPausedAt),
      slaResumedAt: toIso(p.slaResumedAt),
      citizenDeadline: toIso(p.citizenDeadline),
      extensionGrantedAt: toIso(p.extensionGrantedAt),
      infoReminderSentAt: toIso(p.infoReminderSentAt),
      infoFinalReminderSentAt: toIso(p.infoFinalReminderSentAt),
      desistimientoDueNotifiedAt: toIso(p.desistimientoDueNotifiedAt),
      desistimientoAt: toIso(p.desistimientoAt),
      // Recurso de reposición against the desistimiento.
      desistimientoNotifiedAt: toIso(p.desistimientoNotifiedAt),
      desistimientoFinalAt: toIso(p.desistimientoFinalAt),
      desistimientoRevokedAt: toIso(p.desistimientoRevokedAt),
      appealWindowStartsAt: toIso(p.appealWindowStartsAt),
      appealReminderAt: toIso(p.appealReminderAt),
      appealLastDay: toIso(p.appealLastDay),
      appealDeadline: toIso(p.appealDeadline),
      appealReminderSentAt: toIso(p.appealReminderSentAt),
      appealWindowClosedAt: toIso(p.appealWindowClosedAt),
      appeal: p.appeal
        ? {
            ...p.appeal,
            filedAt: toIso(p.appeal.filedAt),
            decisionDueAt: toIso(p.appeal.decisionDueAt),
            decidedAt: toIso(p.appeal.decidedAt),
          }
        : null,
      returnedAt: toIso(p.returnedAt),
      lastCitizenEmailAt: toIso(p.lastCitizenEmailAt),
      responses,
      activeResponseFlows,
    },
  };
};

// Append-only audit event helper. The eventId is deterministic so a repeated
// write (e.g. a transaction retry) won't duplicate the row in events[].
// The detail page reloads itself when it gets this, so no "recarga la página".
export const PQRSD_ANNULLED_ERROR =
  "Esta PQRSD fue anulada mientras la tenías abierta; ya no admite cambios.";

// The detail page is not live, so a tab opened before an annulment still shows
// the old buttons. Every write it can trigger checks the current status first,
// in the same transaction, or assigning would flip an annulled PQRSD back to
// "asignado".
const assertNotAnnulled = (data) => {
  if (data?.pqrsd?.status === PQRSD_STATUS.ANULADO) throw new Error(pqrsdText(PQRSD_ANNULLED_ERROR));
};

const updatePqrsdUnlessAnnulled = (radicado, patch) =>
  runTransaction(firestore, async (tx) => {
    const ref = doc(firestore, COLLECTION, radicado);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error(pqrsdText("PQRSD no encontrada"));
    assertNotAnnulled(snap.data());
    tx.update(ref, patch);
  });

const buildEvent = ({ eventType, radicado, actor, extra = {}, idKey }) => {
  const at = new Date().toISOString();
  return {
    eventType,
    eventId: `${eventType}:${radicado}:${idKey || actor?.userId || "system"}:${Date.now()}`,
    at,
    fecha: at,
    byUserId: actor?.userId || "system",
    byName: actor?.userName || actor?.name || "Sistema",
    porUserId: actor?.userId || "system",
    porNombre: actor?.userName || actor?.name || "Sistema",
    ...extra,
  };
};

export const listPqrsd = async ({ status, assignee, limit: max = 100 } = {}) => {
  try {
    const clauses = [where("filedType", "==", "pqrsd")];
    if (status) clauses.push(where("pqrsd.status", "==", status));
    if (assignee) clauses.push(where("pqrsd.assignee", "==", assignee));
    const q = query(
      collection(firestore, COLLECTION),
      ...clauses,
      orderBy("createdAt", "desc"),
      limit(max)
    );
    const snap = await getDocs(q);
    return snap.docs.map(mapFiled);
  } catch (e) {
    console.error("listPqrsd error:", e);
    return [];
  }
};

export const subscribePqrsdList = ({ status, assignee } = {}, cb) => {
  const clauses = [where("filedType", "==", "pqrsd")];
  if (status) clauses.push(where("pqrsd.status", "==", status));
  if (assignee) clauses.push(where("pqrsd.assignee", "==", assignee));
  const q = query(
    collection(firestore, COLLECTION),
    ...clauses,
    orderBy("createdAt", "desc"),
    limit(200)
  );
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map(mapFiled));
  });
};

// Cursor-paginated fetch for the admin list. `cursor` is a Firestore
// DocumentSnapshot from a prior page (or null for the first page). Returns the
// next page plus the snapshot needed to resume.
//
// `dateFrom` / `dateTo` are JS Date objects bounding `createdAt`. Range queries
// on `createdAt` compose with the existing `orderBy("createdAt", "desc")`
// without extra indexes (Firestore allows range on the ordered field).
export const fetchPqrsdPage = async ({
  status,
  assignee,
  assigneeContains,
  groupId,
  groupIds,
  needsReview,
  dateFrom,
  dateTo,
  cursor,
  pageSize = 25,
} = {}) => {
  try {
    const constraints = [where("filedType", "==", "pqrsd")];
    if (status) constraints.push(where("pqrsd.status", "==", status));
    // "Mis PQRSDs" scope: match any of the (possibly multiple) assignees.
    if (assigneeContains) {
      constraints.push(where("pqrsd.assigneeIds", "array-contains", assigneeContains));
    } else if (assignee) {
      constraints.push(where("pqrsd.assignee", "==", assignee));
    }
    // "Mi dependencia" scope: PQRSDs the AI routed to one of the user's groups.
    // `groupIds` (the groups a user belongs to) takes precedence over a single
    // `groupId`. Firestore "in" supports up to 30 values — plenty for one user.
    if (Array.isArray(groupIds) && groupIds.length) {
      constraints.push(where("pqrsd.groupId", "in", groupIds.slice(0, 30)));
    } else if (groupId) {
      constraints.push(where("pqrsd.groupId", "==", groupId));
    }
    // "Triage" scope: AI couldn't classify confidently or failed.
    if (needsReview) {
      constraints.push(where("pqrsd.aiStatus", "in", ["needs_review", "failed"]));
    }
    if (dateFrom) constraints.push(where("createdAt", ">=", dateFrom));
    if (dateTo) constraints.push(where("createdAt", "<=", dateTo));
    constraints.push(orderBy("createdAt", "desc"));
    if (cursor) constraints.push(startAfter(cursor));
    // Fetch one extra to detect hasMore without a count query.
    constraints.push(limit(pageSize + 1));
    const snap = await getDocs(query(collection(firestore, COLLECTION), ...constraints));
    const pageDocs = snap.docs.slice(0, pageSize);
    return {
      items: pageDocs.map(mapFiled),
      lastDoc: pageDocs[pageDocs.length - 1] || null,
      hasMore: snap.docs.length > pageSize,
    };
  } catch (e) {
    console.error("fetchPqrsdPage error:", e);
    return { items: [], lastDoc: null, hasMore: false };
  }
};

export const getPqrsd = async (radicado) => {
  try {
    const snap = await getDoc(doc(firestore, COLLECTION, radicado));
    if (!snap.exists()) return null;
    return mapFiled(snap);
  } catch (e) {
    console.error("getPqrsd error:", e);
    return null;
  }
};

// Fetches the aggregated PQRSD analytics for the Informes dashboard. Unlike the
// cursor-paginated list, totals must be computed server-side over the whole
// dataset, so this calls the `pqrsdStats` Cloud Function (which also enforces
// the `pqrsd.reports` permission from the caller's token). `from`/`to` are
// optional ISO date strings; omit them for an all-time report. Returns the
// stats body, or null on error (service-returns-null convention).
export const getPqrsdStats = async ({ from, to } = {}) => {
  try {
    const token = await getToken();
    const url =
      process.env.REACT_APP_PQRSD_STATS_URL || pqrsdFunctionUrl("pqrsdStats");
    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ from: from || null, to: to || null }),
    });
    const json = await resp.json().catch(() => null);
    if (!resp.ok || !json?.result) return null;
    return json.body;
  } catch (e) {
    console.error("getPqrsdStats error:", e);
    return null;
  }
};

// Excel reports (Informes PQRSD → Informes), served by pqrsdStats action
// REPORT. They answer { body, error } like the user reports: the page must
// tell "you may not see this" (FORBIDDEN) from a failure (FAILED), and a
// failed .xlsx falls back to a CSV built here from the JSON rows.
const callPqrsdReport = async (body) => {
  try {
    const token = await getToken();
    const url = process.env.REACT_APP_PQRSD_STATS_URL || pqrsdFunctionUrl("pqrsdStats");
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ action: "REPORT", ...body }),
    });
    if (resp.status === 401 || resp.status === 403) return { body: null, error: "FORBIDDEN" };
    const json = await resp.json().catch(() => null);
    if (!resp.ok || !json?.result) return { body: null, error: "FAILED" };
    return { body: json.body, error: null };
  } catch (e) {
    console.error(`pqrsd report ${body.report} error:`, e);
    return { body: null, error: "FAILED" };
  }
};

// { report, from, to } → { body: { title, period, columns, rows, total }, error }.
export const previewPqrsdReport = (filters) => callPqrsdReport({ ...filters, format: "json" });

// Builds the .xlsx on the server and saves it. Returns null when downloaded,
// else the error code.
export const downloadPqrsdReportXlsx = async (filters) => {
  const { body, error } = await callPqrsdReport({ ...filters, format: "xlsx" });
  if (error || !body?.base64) return error || "FAILED";
  try {
    const bytes = Uint8Array.from(atob(body.base64), (c) => c.charCodeAt(0));
    saveBlob(new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), body.fileName);
    return null;
  } catch (e) {
    console.error("pqrsd report xlsx save error:", e);
    return "FAILED";
  }
};

// The browser-built fallback: UTF-8 with BOM so Excel opens accents right, and
// the period on top like the .xlsx.
export const downloadPqrsdReportCsv = (fileName, { title, period, columns, rows }) => {
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",;\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    [title], [period], [],
    columns.map((c) => c.label),
    ...rows,
  ].map((r) => r.map(escape).join(","));
  saveBlob(new Blob([`\ufeff${lines.join("\r\n")}`], { type: "text/csv;charset=utf-8;" }), fileName);
};

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoked a moment later: revoking right after click() can cancel the download.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// Normalizes the assignee argument (single object or array) into a clean
// array of { userId, name, email } entries, de-duped by userId.
const normalizeAssignees = (input) => {
  const arr = Array.isArray(input) ? input : input ? [input] : [];
  const seen = new Set();
  const out = [];
  for (const a of arr) {
    if (!a?.userId || seen.has(a.userId)) continue;
    seen.add(a.userId);
    out.push({
      userId: a.userId,
      name: a.name || `${a.firstName || ""} ${a.lastName || ""}`.trim() || null,
      email: a.email || null,
    });
  }
  return out;
};

// Assigns a PQRSD to one or more internal users and posts a task into each
// inbox. `assignees` may be a single { userId, name, email } object or an array
// of them. The first entry is mirrored into the legacy single `assignee*`
// fields for back-compat; `assigneeIds[]` powers the "Mis PQRSDs" query.
// `actor` is the supervisor making the change (for the task's notes/audit).
export const assignPqrsd = async (radicado, assignees, actor = null) => {
  const list = normalizeAssignees(assignees);
  if (list.length === 0) throw new Error("At least one assignee is required");
  const primary = list[0];
  try {
    const event = buildEvent({
      eventType: "PQRSD_ASSIGNED",
      radicado,
      actor,
      idKey: `asg-${list.map((a) => a.userId).join("-")}`,
      extra: {
        toUserId: primary.userId,
        toUserName: primary.name || null,
        toUserIds: list.map((a) => a.userId),
        toUserNames: list.map((a) => a.name || null).filter(Boolean).join(", ") || null,
      },
    });
    await updatePqrsdUnlessAnnulled(radicado, {
      "pqrsd.assignees": list,
      "pqrsd.assigneeIds": list.map((a) => a.userId),
      // Legacy single-assignee mirror (first pick) for back-compat reads.
      "pqrsd.assignee": primary.userId,
      "pqrsd.assigneeName": primary.name || null,
      "pqrsd.assigneeEmail": primary.email || null,
      "pqrsd.assignedAt": new Date(),
      "pqrsd.assignedBy": actor?.userId || null,
      "pqrsd.status": "asignado",
      events: arrayUnion(event),
    });

    // Notify every assignee via the existing tasks collection so the PQRSD
    // shows up in each one's My Tasks view.
    await Promise.all(
      list.map((a) =>
        addDoc(collection(firestore, "tasks"), {
          userId: a.userId,
          taskName: pqrsdText(`PQRSD asignada: ${radicado}`),
          description: pqrsdText(`Se te asignó la PQRSD ${radicado}. Revísala y da trámite dentro del término.`),
          stepId: "init",
          kanbanId: "Area1",
          priority: "alta",
          taskType: "pqrsd",
          status: "open",
          createdAt: serverTimestamp(),
          filedId: radicado,
          meta: {
            assignedBy: actor?.userId || null,
            assignedByName: actor?.userName || actor?.name || null,
            radicado,
            link: `/pqrsd/mine/${encodeURIComponent(radicado)}`,
          },
        })
      )
    );

    // Notify each assignee (best-effort; the assignment already committed).
    try {
      const fresh = await getPqrsd(radicado);
      const p = fresh?.pqrsd || {};
      await notifyStaffAssigned({
        radicado,
        assignees: list,
        subjectLine: fresh?.subject || null,
        type: p.type || null,
        slaDeadline: p.slaDeadline || null,
        anonymous: !!p.anonymous,
        actor,
      });
    } catch (mailErr) {
      console.warn("assignPqrsd email failed (non-fatal):", mailErr?.message);
    }

    return true;
  } catch (e) {
    console.error("assignPqrsd error:", e);
    // Returning false let the caller close the dialog as if it had worked.
    throw e;
  }
};

// Supervisor override of the AI classification. Corrects the type/priority and
// re-routes to a chosen dependency group (or clears it back to triage). When a
// group is set, every member gets a task so the whole group sees it. Logs an
// audit event either way. `group` is { id, name, members } or null.
export const overridePqrsdClassification = async (radicado, { type, priority, group }, actor = null) => {
  try {
    const members = Array.isArray(group?.members) ? group.members.filter((m) => m?.userId) : [];
    const event = buildEvent({
      eventType: "PQRSD_AI_ROUTED",
      radicado,
      actor,
      idKey: `override-${group?.id || "triage"}`,
      extra: {
        groupId: group?.id || null,
        groupName: group?.name || null,
        manual: true,
        type: type || null,
        priority: priority || null,
      },
    });
    const patch = {
      "pqrsd.aiStatus": group ? "classified" : "needs_review",
      "pqrsd.groupId": group?.id || null,
      "pqrsd.groupName": group?.name || null,
      "pqrsd.groupAssignedAt": group ? new Date() : null,
      events: arrayUnion(event),
    };
    if (type) patch["pqrsd.type"] = type;
    if (priority !== undefined) patch["pqrsd.priority"] = priority || null;
    await updatePqrsdUnlessAnnulled(radicado, patch);

    if (members.length) {
      await Promise.all(
        members.map((m) =>
          addDoc(collection(firestore, "tasks"), {
            userId: m.userId,
            taskName: pqrsdText(`PQRSD enrutada a ${group.name || "tu dependencia"}: ${radicado}`),
            description: pqrsdText(`Se enrutó la PQRSD ${radicado} a tu dependencia. Revísala y define quién la atiende.`),
            stepId: "init",
            kanbanId: "Area1",
            priority: "alta",
            taskType: "pqrsd",
            status: "open",
            createdAt: serverTimestamp(),
            filedId: radicado,
            meta: {
              routedBy: actor?.userId || null,
              routedByName: actor?.userName || actor?.name || null,
              groupName: group.name || null,
              radicado,
              link: `/pqrsd/dependencia/${encodeURIComponent(radicado)}`,
            },
          })
        )
      );
    }
    return true;
  } catch (e) {
    console.error("overridePqrsdClassification error:", e);
    throw e;
  }
};

// Full-admin only: ask the backend to re-run the AI classifier on an existing
// PQRSD and overlay the fresh result (type / priority / routing). Server-side
// enforces the ADMIN role from the caller's Firebase token, so this is safe to
// gate on the client purely for UX. Returns the new classification summary.
export const retryPqrsdClassification = async (radicado, user) => {
  const resp = await fetch(pqrsdFunctionUrl("handlePqrsd"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + user.token,
    },
    body: JSON.stringify({ action: "reclassify", radicado }),
  });
  const json = await resp.json().catch(() => null);
  if (!resp.ok || !json?.result) {
    throw new Error(json?.error || "No fue posible reintentar la clasificación por IA.");
  }
  return json;
};

export const setPqrsdInProgress = async (radicado, actor = null) => {
  try {
    await updatePqrsdUnlessAnnulled(radicado, {
      "pqrsd.status": "en_tramite",
    });

    // Let the citizen know an operator is now working on it (best-effort).
    try {
      const fresh = await getPqrsd(radicado);
      const citizenEmail = fresh?.pqrsd?.email || null;
      if (citizenEmail) {
        await notifyCitizenInProgress({ radicado, to: citizenEmail, actor });
      }
    } catch (mailErr) {
      console.warn("setPqrsdInProgress email failed (non-fatal):", mailErr?.message);
    }

    return true;
  } catch (e) {
    console.error("setPqrsdInProgress error:", e);
    // The caller shows e.message; returning false left the click doing nothing.
    throw e;
  }
};

export const closePqrsd = async (radicado) => {
  try {
    await updatePqrsdUnlessAnnulled(radicado, {
      "pqrsd.status": "cerrado",
      "pqrsd.closedAt": new Date(),
    });
    return true;
  } catch (e) {
    console.error("closePqrsd error:", e);
    // The caller shows e.message; returning false left the click doing nothing.
    throw e;
  }
};

// --- PQRSD response approval workflow ---------------------------------------

// Kicks off the supervised PQRSD answer. The startPqrsdResponseFlow Cloud
// Function copies the chosen answer template (Config → PQRSD → Documentos),
// fills its tags, creates the flow + first task and updates the parent PQRSD's
// responseFlow* fields.
//
// `steps` is the chain the operator picked, in order:
// [{ type: 'elaborate', userId }, { type: 'review'|'approve', userId }*, { type: 'sign', userId }].
// Owners are resolved server-side from each user's record.
//
// `responseType` is 'interim' (operator-driven SLA extension, capped per
// cycle) or 'final' (citizen-visible confirm/reopen choice).
export const startResponseFlow = async ({
  parentRadicadoId,
  answerTemplateId,
  steps,
  subject,
  responseType = "final",
}, user) => {
  try {
    // Force-refresh the Firebase ID token so the call doesn't fail with
    // 403 Unauthorized after the cached Redux token (1h TTL) expires.
    const freshToken = (await getToken()) || user.token;
    const resp = await fetch(
      "https://" + server + ".cloudfunctions.net/startPqrsdResponseFlow",
      {
        method: "post",
        body: JSON.stringify({
          parentRadicadoId, answerTemplateId, steps, subject, responseType,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + freshToken,
        },
      }
    );
    const json = await resp.json();
    if (!resp.ok || !json.result) {
      throw new Error(json?.error || "No fue posible iniciar el flujo de respuesta");
    }
    return {
      flowId: json.flowId,
      outgoingRadicado: json.outgoingRadicado,
      taskId: json.taskId,
    };
  } catch (e) {
    console.error("startResponseFlow error:", e);
    throw e;
  }
};

// --- Return / forward / reassign -------------------------------------------

// Operator declines the assignment. The PQRSD lands back in the admin queue
// with status `devuelto`, no assignee, and a recorded reason. Admin reassigns
// with reassignPqrsd. Open tasks for the prior assignee are cancelled.
export const returnPqrsd = async (radicado, reason, actor = null) => {
  if (!reason || String(reason).trim().length < 10) {
    throw new Error("Motivo de devolución requerido (mínimo 10 caracteres).");
  }
  try {
    const filedRef = doc(firestore, COLLECTION, radicado);
    let returnedBy = null;
    let isAnonymous = false;
    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(filedRef);
      if (!snap.exists()) throw new Error(pqrsdText("PQRSD no encontrada"));
      const data = snap.data();
      assertNotAnnulled(data);
      const p = data.pqrsd || {};
      const previousAssignee = {
        userId: p.assignee || null,
        name: p.assigneeName || null,
      };
      returnedBy = previousAssignee.name;
      isAnonymous = !!p.anonymous;
      const event = buildEvent({
        eventType: "PQRSD_RETURNED",
        radicado,
        actor,
        idKey: `ret-${(p.returnCount || 0) + 1}`,
        extra: {
          reason: String(reason).trim(),
          fromUserId: previousAssignee.userId,
          fromUserName: previousAssignee.name,
        },
      });
      tx.update(filedRef, {
        "pqrsd.status": PQRSD_STATUS.DEVUELTO,
        "pqrsd.assignee": null,
        "pqrsd.assigneeName": null,
        "pqrsd.assigneeEmail": null,
        "pqrsd.assignees": [],
        "pqrsd.assigneeIds": [],
        // Devolver doubles as the correction path for an AI misroute: clear the
        // group and flag for manual triage so a supervisor re-classifies/re-routes.
        "pqrsd.groupId": null,
        "pqrsd.groupName": null,
        "pqrsd.aiStatus": "needs_review",
        "pqrsd.returnedAt": new Date(),
        "pqrsd.returnedReason": String(reason).trim(),
        "pqrsd.returnCount": increment(1),
        events: arrayUnion(event),
      });
    });

    // Cancel any open task for the prior assignee.
    await cancelOpenPqrsdTasks(radicado);

    // Notify the PQRSD queue so a supervisor reassigns it (best-effort).
    try {
      await notifyStaffReturned({
        radicado,
        fromName: returnedBy,
        reason: String(reason).trim(),
        anonymous: isAnonymous,
        actor,
      });
    } catch (mailErr) {
      console.warn("returnPqrsd email failed (non-fatal):", mailErr?.message);
    }

    return true;
  } catch (e) {
    console.error("returnPqrsd error:", e);
    throw e;
  }
};

// Dependency supervisor bounces a misrouted PQRSD back to Triage. Unlike the
// operator "devolver" (returnPqrsd), this is not gated on being the assignee and
// resets the routing (clears the group + assignees, aiStatus → needs_review) so
// the general supervisor re-routes it to another dependency from the Triage
// queue. The SLA clock is deliberately preserved (slaDeadline untouched): the
// citizen's term keeps running through a routing correction.
export const returnPqrsdToTriage = async (radicado, reason, actor = null) => {
  if (!reason || String(reason).trim().length < 10) {
    throw new Error("Motivo requerido (mínimo 10 caracteres).");
  }
  try {
    const filedRef = doc(firestore, COLLECTION, radicado);
    let returnedByName = null;
    let isAnonymous = false;
    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(filedRef);
      if (!snap.exists()) throw new Error(pqrsdText("PQRSD no encontrada"));
      const data = snap.data();
      assertNotAnnulled(data);
      const p = data.pqrsd || {};
      returnedByName = actor?.userName || actor?.name || null;
      isAnonymous = !!p.anonymous;
      const event = buildEvent({
        eventType: "PQRSD_RETURNED_TO_TRIAGE",
        radicado,
        actor,
        idKey: `rtt-${(p.triageReturnCount || 0) + 1}`,
        extra: {
          reason: String(reason).trim(),
          fromGroupId: p.groupId || null,
          fromGroupName: p.groupName || null,
          fromAssigneeIds: Array.isArray(p.assigneeIds) ? p.assigneeIds : [],
        },
      });
      tx.update(filedRef, {
        "pqrsd.status": PQRSD_STATUS.RADICADO,
        "pqrsd.assignee": null,
        "pqrsd.assigneeName": null,
        "pqrsd.assigneeEmail": null,
        "pqrsd.assignees": [],
        "pqrsd.assigneeIds": [],
        // Clear routing so it re-enters Triage for a fresh (manual) re-route.
        "pqrsd.groupId": null,
        "pqrsd.groupName": null,
        "pqrsd.aiStatus": "needs_review",
        "pqrsd.returnedToTriageAt": new Date(),
        "pqrsd.returnedToTriageReason": String(reason).trim(),
        "pqrsd.triageReturnCount": increment(1),
        events: arrayUnion(event),
      });
    });

    // Cancel any open task for the prior assignee(s).
    await cancelOpenPqrsdTasks(radicado);

    // Notify the supervisor queue so the general supervisor re-routes it.
    try {
      await notifyStaffReturned({
        radicado,
        fromName: returnedByName,
        reason: String(reason).trim(),
        anonymous: isAnonymous,
        actor,
      });
    } catch (mailErr) {
      console.warn("returnPqrsdToTriage email failed (non-fatal):", mailErr?.message);
    }

    return true;
  } catch (e) {
    console.error("returnPqrsdToTriage error:", e);
    throw e;
  }
};

// Operator hands the PQRSD off to another user. Status stays `en_tramite`.
export const forwardPqrsd = async (radicado, toUser, note = null, actor = null) => {
  if (!toUser?.userId) throw new Error("Destinatario requerido.");
  try {
    const filedRef = doc(firestore, COLLECTION, radicado);
    let forwardedFromName = null;
    let isAnonymous = false;
    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(filedRef);
      if (!snap.exists()) throw new Error(pqrsdText("PQRSD no encontrada"));
      const data = snap.data();
      assertNotAnnulled(data);
      const p = data.pqrsd || {};
      const fromUser = { userId: p.assignee || null, name: p.assigneeName || null };
      forwardedFromName = fromUser.name;
      isAnonymous = !!p.anonymous;
      const event = buildEvent({
        eventType: "PQRSD_FORWARDED",
        radicado,
        actor,
        idKey: `fwd-${toUser.userId}`,
        extra: {
          note: note ? String(note).trim() : null,
          fromUserId: fromUser.userId,
          fromUserName: fromUser.name,
          toUserId: toUser.userId,
          toUserName: toUser.name || null,
        },
      });
      tx.update(filedRef, {
        "pqrsd.assignee": toUser.userId,
        "pqrsd.assigneeName": toUser.name || null,
        "pqrsd.assigneeEmail": toUser.email || null,
        "pqrsd.assignees": [
          { userId: toUser.userId, name: toUser.name || null, email: toUser.email || null },
        ],
        "pqrsd.assigneeIds": [toUser.userId],
        "pqrsd.status": PQRSD_STATUS.EN_TRAMITE,
        events: arrayUnion(event),
      });
    });

    await cancelOpenPqrsdTasks(radicado);
    await addDoc(collection(firestore, "tasks"), {
      userId: toUser.userId,
      taskName: pqrsdText(`PQRSD reenviada: ${radicado}`),
      description: pqrsdText(`Te reenviaron la PQRSD ${radicado}. Continúa la gestión.`),
      stepId: "init",
      kanbanId: "Area1",
      priority: "alta",
      taskType: "pqrsd",
      status: "open",
      createdAt: serverTimestamp(),
      filedId: radicado,
      meta: {
        forwardedBy: actor?.userId || null,
        forwardedByName: actor?.userName || actor?.name || null,
        radicado,
        link: `/pqrsd/mine/${encodeURIComponent(radicado)}`,
      },
    });

    // Notify the new owner (best-effort).
    try {
      await notifyStaffForwarded({
        radicado,
        to: toUser.email,
        fromName: forwardedFromName,
        anonymous: isAnonymous,
        actor,
      });
    } catch (mailErr) {
      console.warn("forwardPqrsd email failed (non-fatal):", mailErr?.message);
    }

    return true;
  } catch (e) {
    console.error("forwardPqrsd error:", e);
    throw e;
  }
};

// Admin reassigns a `devuelto` PQRSD to one or more new operators. SLA window
// resets to the full type-specific window so the new owners have the full term.
// `toUsers` may be a single object or an array of { userId, name, email }.
export const reassignPqrsd = async (radicado, toUsers, actor = null) => {
  const list = normalizeAssignees(toUsers);
  if (list.length === 0) throw new Error("Operario destino requerido.");
  const primary = list[0];
  try {
    const filedRef = doc(firestore, COLLECTION, radicado);
    let newDeadline = null;
    let subjectLine = null;
    let pqrsdType = null;
    let isAnonymous = false;
    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(filedRef);
      if (!snap.exists()) throw new Error(pqrsdText("PQRSD no encontrada"));
      const data = snap.data();
      assertNotAnnulled(data);
      const p = data.pqrsd || {};
      subjectLine = data.subject || null;
      pqrsdType = p.type || null;
      isAnonymous = !!p.anonymous;
      const slaDays = PQRSD_SLA_DAYS[p.type] || 15;
      const now = new Date();
      newDeadline = addBusinessDays(now, slaDays);
      const event = buildEvent({
        eventType: "PQRSD_REASSIGNED",
        radicado,
        actor,
        idKey: `ras-${list.map((a) => a.userId).join("-")}`,
        extra: {
          toUserId: primary.userId,
          toUserName: primary.name || null,
          toUserIds: list.map((a) => a.userId),
          previousReturnReason: p.returnedReason || null,
        },
      });
      const slaResetEvent = buildEvent({
        eventType: "PQRSD_SLA_RESET",
        radicado,
        actor,
        idKey: `ras-sla-${primary.userId}`,
        extra: { trigger: "reassign", deadline: newDeadline.toISOString() },
      });
      tx.update(filedRef, {
        "pqrsd.assignees": list,
        "pqrsd.assigneeIds": list.map((a) => a.userId),
        "pqrsd.assignee": primary.userId,
        "pqrsd.assigneeName": primary.name || null,
        "pqrsd.assigneeEmail": primary.email || null,
        "pqrsd.assignedAt": now,
        "pqrsd.assignedBy": actor?.userId || null,
        "pqrsd.status": PQRSD_STATUS.ASIGNADO,
        "pqrsd.slaDeadline": newDeadline,
        "pqrsd.slaBaseDate": now,
        "pqrsd.slaResetCount": increment(1),
        "pqrsd.slaWarningSentAt": null,
        "pqrsd.slaBreached": false,
        "pqrsd.slaBreachedNotifiedAt": null,
        events: arrayUnion(event, slaResetEvent),
      });
    });

    await Promise.all(
      list.map((a) =>
        addDoc(collection(firestore, "tasks"), {
          userId: a.userId,
          taskName: pqrsdText(`PQRSD reasignada: ${radicado}`),
          description: pqrsdText(`Se te reasignó la PQRSD ${radicado}. Tienes el plazo completo.`),
          stepId: "init",
          kanbanId: "Area1",
          priority: "alta",
          taskType: "pqrsd",
          status: "open",
          createdAt: serverTimestamp(),
          filedId: radicado,
          meta: {
            reassignedBy: actor?.userId || null,
            reassignedByName: actor?.userName || actor?.name || null,
            radicado,
            link: `/pqrsd/mine/${encodeURIComponent(radicado)}`,
          },
        })
      )
    );

    // Notify each new operator with the full (reset) term (best-effort).
    try {
      await notifyStaffReassigned({
        radicado,
        assignees: list,
        subjectLine,
        type: pqrsdType,
        slaDeadline: newDeadline,
        anonymous: isAnonymous,
        actor,
      });
    } catch (mailErr) {
      console.warn("reassignPqrsd email failed (non-fatal):", mailErr?.message);
    }

    return { ok: true, newDeadline };
  } catch (e) {
    console.error("reassignPqrsd error:", e);
    throw e;
  }
};

// Cancel any open `tasks/*` row attached to this PQRSD radicado. Used on
// return + forward so a stale task doesn't linger in the prior owner's inbox.
async function cancelOpenPqrsdTasks(radicado) {
  try {
    const q = query(
      collection(firestore, "tasks"),
      where("taskType", "==", "pqrsd"),
      where("filedId", "==", radicado),
      where("status", "==", "open")
    );
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map((d) =>
      updateDoc(d.ref, { status: "cancelled", cancelledAt: serverTimestamp() })
    ));
  } catch (e) {
    console.warn("cancelOpenPqrsdTasks error (non-fatal):", e?.message);
  }
}

// Convenience selector used by the admin detail UI to render the responses[]
// list chronologically. Uses the cached pqrsd doc fields directly.
export const getPqrsdResponses = (pqrsdDoc) => {
  const list = pqrsdDoc?.pqrsd?.responses;
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => (a.index || 0) - (b.index || 0));
};

// --- Requerimiento de información al ciudadano (CPACA art. 17) --------------
//
// Every write goes through handlePqrsd: sending a requerimiento allocates a
// radicado for the outgoing communication, signs a scoped citizen token and
// suspends the legal term, none of which can be done from the browser. Reads
// come straight from the subcollection, like the rest of the module.

const callInfoRequest = async (payload, user, fallbackError) => {
  const resp = await fetch(pqrsdFunctionUrl("handlePqrsd"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + user.token,
    },
    body: JSON.stringify(payload),
  });
  const json = await resp.json().catch(() => null);
  if (!resp.ok || !json?.result) throw new Error(json?.error || fallbackError);
  return json;
};

// Saves a draft requerimiento without notifying anyone (the term keeps running).
export const saveInfoRequest = (radicado, { requestId, items, note }, user) =>
  callInfoRequest(
    { action: "info_request", op: "save", radicado, requestId, items, note },
    user,
    "No fue posible guardar el requerimiento."
  );

// Sends it (or leaves it pending_approval when the institution requires a
// reviewer — the response carries `requiresApproval`).
export const sendInfoRequest = (radicado, { requestId, items, note }, user) =>
  callInfoRequest(
    { action: "info_request", op: "send", radicado, requestId, items, note },
    user,
    "No fue posible enviar el requerimiento."
  );

// Puts a draft under review and (optionally) tells the named reviewers. The
// term keeps running: nothing has been required of the citizen yet.
export const submitInfoRequestForReview = (radicado, requestId, user, { reviewerIds = [] } = {}) =>
  callInfoRequest(
    { action: "info_request", op: "submit_review", radicado, requestId, reviewerIds },
    user,
    "No fue posible enviar el requerimiento a revisión."
  );

// Pulls one's own requerimiento back out of the approval queue, without
// destroying it. A reviewer returning it (reject) and deleting the draft were
// the only exits before.
export const retractInfoRequest = (radicado, requestId, user) =>
  callInfoRequest(
    { action: "info_request", op: "retract", radicado, requestId },
    user,
    "No fue posible retirar el requerimiento de la cola."
  );

// The approval queue, read as an inbox. Answered by a single-field equality on
// the parent filed doc rather than a collection-group sweep of the
// requerimientos themselves, so it needs no extra index.
export const subscribePendingApprovals = (onChange) => {
  try {
    const q = query(
      collection(firestore, COLLECTION),
      where("pqrsd.infoRequestPending", "==", true)
    );
    return onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          const p = data.pqrsd || {};
          return {
            radicado: data.filedId || d.id,
            referenceId: data.referenceId || null,
            subject: data.subject || p.subject || "",
            type: p.type || null,
            requestId: p.infoRequestPendingId || null,
            submittedAt: toIso(p.infoRequestPendingAt),
            submittedBy: p.infoRequestPendingBy || null,
            submittedByName: p.infoRequestPendingByName || "",
            reviewerIds: Array.isArray(p.infoRequestReviewerIds) ? p.infoRequestReviewerIds : [],
            slaDeadline: toIso(p.slaDeadline),
            status: p.status || null,
            // Which dependency is waiting, so the queue can say so and filter on it.
            groupId: p.groupId || null,
            groupName: p.groupName || "",
          };
        });
        // Oldest first: the whole point is that the term keeps running while
        // one of these waits.
        rows.sort((a, b) => String(a.submittedAt || "").localeCompare(String(b.submittedAt || "")));
        onChange(rows);
      },
      (e) => {
        console.error("subscribePendingApprovals error:", e);
        onChange([]);
      }
    );
  } catch (e) {
    console.error("subscribePendingApprovals error:", e);
    onChange([]);
    return () => {};
  }
};

// How many PQRSDs the AI could not route, for the Triage badge. Live, because
// an unrouted PQRSD is burning its term in nobody's queue: it is assigned to no
// one and sitting in no dependency, so until a person triages it there is
// nothing anywhere else in the app that would show it.
//
// Capped: the badge only has to say "there is work and roughly how much".
export const subscribeTriageCount = (onChange, { cap = 100 } = {}) => {
  try {
    const q = query(
      collection(firestore, COLLECTION),
      where("filedType", "==", "pqrsd"),
      where("pqrsd.aiStatus", "in", ["needs_review", "failed"]),
      limit(cap)
    );
    return onSnapshot(
      q,
      (snap) => onChange(snap.size),
      (e) => {
        console.error("subscribeTriageCount error:", e);
        onChange(0);
      }
    );
  } catch (e) {
    console.error("subscribeTriageCount error:", e);
    onChange(0);
    return () => {};
  }
};

// The requerimientos whose plazo ciudadano ran out without an answer. The sweep
// raises `pqrsd.desistimientoDue` and stops there — deciding what happens next
// is a human's job (CPACA art. 17 requires an acto administrativo motivado), and
// until someone does it the PQRSD sits suspended with nobody looking at it. This
// is the feed that puts those in front of the dependency supervisor.
export const subscribeExpiredInfoRequests = (onChange) => {
  try {
    const q = query(
      collection(firestore, COLLECTION),
      where("pqrsd.desistimientoDue", "==", true)
    );
    return onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          // The flag is raised by the sweep and cleared by every exit (reply,
          // retiro, desistimiento). A doc that left pendiente_info still
          // carrying it is stale, and counting it would overstate the badge —
          // which is the one number a supervisor is meant to trust.
          .filter((d) => (d.data().pqrsd || {}).status === PQRSD_STATUS.PENDIENTE_INFO)
          .map((d) => {
          const data = d.data();
          const p = data.pqrsd || {};
          return {
            radicado: data.filedId || d.id,
            subject: data.subject || p.subject || "",
            type: p.type || null,
            status: p.status || null,
            groupId: p.groupId || null,
            groupName: p.groupName || "",
            assigneeName: p.assigneeName || "",
            requestId: p.activeInfoRequestId || null,
            citizenDeadline: toIso(p.citizenDeadline),
            expiredNotifiedAt: toIso(p.desistimientoDueNotifiedAt),
          };
        });
        // Longest overdue first: every day one of these waits is a day the
        // petition stays parked with no decision on it.
        rows.sort((a, b) => String(a.citizenDeadline || "").localeCompare(String(b.citizenDeadline || "")));
        onChange(rows);
      },
      (e) => {
        console.error("subscribeExpiredInfoRequests error:", e);
        onChange([]);
      }
    );
  } catch (e) {
    console.error("subscribeExpiredInfoRequests error:", e);
    onChange([]);
    return () => {};
  }
};

// PQRSDs with a recurso de reposición waiting to be decided (CPACA art. 80).
// The status is set when the citizen files it and left only by the decision,
// so it is the whole signal — no flag to go stale. Soonest decision deadline
// first: art. 86 gives two months, and past them silence counts as a denial.
export const subscribePendingAppeals = (onChange) => {
  try {
    const q = query(
      collection(firestore, COLLECTION),
      where("pqrsd.status", "==", PQRSD_STATUS.EN_RECURSO)
    );
    return onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          const p = data.pqrsd || {};
          const appeal = p.appeal || {};
          return {
            radicado: data.filedId || d.id,
            subject: data.subject || p.subject || "",
            type: p.type || null,
            status: p.status || null,
            groupId: p.groupId || null,
            groupName: p.groupName || "",
            assigneeIds: Array.isArray(p.assigneeIds) ? p.assigneeIds : [],
            desistimientoFiledId: p.desistimientoFiledId || null,
            appealFiledAt: toIso(appeal.filedAt),
            decisionDueAt: toIso(appeal.decisionDueAt),
          };
        });
        rows.sort((a, b) => String(a.decisionDueAt || "").localeCompare(String(b.decisionDueAt || "")));
        onChange(rows);
      },
      (e) => {
        console.error("subscribePendingAppeals error:", e);
        onChange([]);
      }
    );
  } catch (e) {
    console.error("subscribePendingAppeals error:", e);
    onChange([]);
    return () => {};
  }
};

// Returns a requerimiento under review to its drafters, with the reason.
export const rejectInfoRequest = (radicado, requestId, comment, user) =>
  callInfoRequest(
    { action: "info_request", op: "reject", radicado, requestId, comment },
    user,
    "No fue posible devolver el requerimiento."
  );

// Discards a requerimiento that never left the entity. One the citizen already
// received is withdrawn, never deleted.
export const deleteInfoRequest = (radicado, requestId, user) =>
  callInfoRequest(
    { action: "info_request", op: "delete", radicado, requestId },
    user,
    "No fue posible eliminar el borrador."
  );

export const approveInfoRequest = (radicado, requestId, user) =>
  callInfoRequest(
    { action: "info_request", op: "approve", radicado, requestId },
    user,
    "No fue posible aprobar el requerimiento."
  );

// Rebuilds the citizen's reply link (and optionally re-sends the email) for a
// requerimiento that is still outstanding. The link is signed on demand from the
// stored tokenId, never persisted.
export const resendInfoRequestLink = (radicado, requestId, user, { sendEmail = false } = {}) =>
  callInfoRequest(
    { action: "info_request", op: "resend", radicado, requestId, sendEmail },
    user,
    "No fue posible generar el enlace del ciudadano."
  );

// Withdraws a requerimiento the citizen has not answered and resumes the term.
export const withdrawInfoRequest = (radicado, requestId, reason, user) =>
  callInfoRequest(
    { action: "info_request", op: "withdraw", radicado, requestId, reason },
    user,
    "No fue posible retirar el requerimiento."
  );

// Grants the prórroga on the citizen's behalf (they can also ask for it from
// their own link).
export const extendInfoRequest = (radicado, requestId, user) =>
  callInfoRequest(
    { action: "info_request", op: "extend", radicado, requestId },
    user,
    "No fue posible conceder la prórroga."
  );

// Decrees the desistimiento tácito: files the acto motivado and archives the
// PQRSD. Server-side requires pqrsd.decree_desistimiento and an expired plazo.
export const decreeDesistimiento = (radicado, reason, user) =>
  callInfoRequest(
    { action: "decree_desistimiento", radicado, reason },
    user,
    "No fue posible decretar el desistimiento."
  );

// Resolves the recurso de reposición against a desistimiento: "grant" revokes
// it and resumes the term, "deny" confirms the archive. Files a motivated acto;
// same permission as decreeing the desistimiento.
export const resolveAppeal = (radicado, decision, reason, user) =>
  callInfoRequest(
    { action: "resolve_appeal", radicado, decision, reason },
    user,
    "No fue posible resolver el recurso de reposición."
  );

export const listInfoRequests = async (radicado) => {
  try {
    const snap = await getDocs(
      query(collection(firestore, COLLECTION, radicado, "infoRequests"), orderBy("createdAt", "asc"))
    );
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: toIso(d.data().createdAt),
      sentAt: toIso(d.data().sentAt),
      dueAt: toIso(d.data().dueAt),
      repliedAt: toIso(d.data().reply?.submittedAt),
    }));
  } catch (e) {
    console.error("listInfoRequests error:", e);
    return [];
  }
};
